import * as React from 'react';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/dist/components/user_context_provider';
import {
  GetBillingMethod,
  UpdateMyShopBillingMethod,
  Input_core_apimessages_UpdateBillingMethodRequest as UpdateBillingMethodRequest,
  core_apimessages_AdyenPaymentResult_PaymentStatus as PaymentStatus,
  core_apimessages_AdyenPaymentResult as AdyenPaymentResult,
  core_apimessages_Error,
  core_apimessages_MyShopOnboardingResponse_BillingMethodStatus as BillingMethodStatus,
} from '../schemas/rql';
import BillingMethodForm from './billing_method_form';
import { connectGetBillingMethod, connectUpdateMyShopBillingMethod } from './billing_method_operations';
import { ChildProps, MutationFn, compose } from 'react-apollo';
import bind from '@reverbdotcom/commons/dist/bind';
import { parseGraphqlErrors } from '@reverbdotcom/commons/dist/parse_graphql_errors';
import BillingMethodPlaceholder from './billing_method_placeholder';
import PrimaryBillingMethod from './primary_billing_method';
import ThreeDS1Container from '../credit_cards/threeds1_container';
import ThreeDS2BillingContainer from './threeds2_billing_container';
import BillingMethodFormError from './billing_method_form_error';
import { BillingMethodResult, isBillingMethodResultSuccessful } from './billing_method_result';

interface MutationProps {
  updateMyShopBillingMethod: MutationFn<UpdateMyShopBillingMethod.Mutation, UpdateMyShopBillingMethod.Variables>;
}

interface ExternalProps {
  onRedirectShopperRequired?(paymentResult: AdyenPaymentResult): void;
  onUpdateBillingMethodSuccess?(): void;
  defaultAdyenPaymentResult?: AdyenPaymentResult;
  returnURL: string;
  description?: string;
  showEditURL?: boolean;
  title?: string;
}
type Props = ExternalProps & IUserContext & MutationProps;

interface State {
  billingMethodResult: BillingMethodResult;
  isSaving: boolean;
  errors: core_apimessages_Error[];
  showBillingMethodForm: boolean;
}

export class BillingMethod extends React.Component<ChildProps<Props, GetBillingMethod.Query>, State> {
  state = {
    billingMethodResult: {
      adyenPaymentResult: this.props.defaultAdyenPaymentResult,
      creditCard: null,
      billingMethodUuid: null,
    },
    isSaving: false,
    errors: [],
    showBillingMethodForm: false,
  };

  get primaryBillingCard() {
    return this.props.data.me.creditCards.find((creditCard) => creditCard.primaryBilling);
  }

  get is3ds1Required() {
    return this.state.billingMethodResult.adyenPaymentResult &&
      this.state.billingMethodResult.adyenPaymentResult.paymentStatus === PaymentStatus.REDIRECT_SHOPPER_REQUIRED;
  }

  get is3ds2Required() {
    return this.state.billingMethodResult.adyenPaymentResult &&
      (this.state.billingMethodResult.adyenPaymentResult.paymentStatus === PaymentStatus.IDENTIFY_SHOPPER_REQUIRED ||
        this.state.billingMethodResult.adyenPaymentResult.paymentStatus === PaymentStatus.CHALLENGE_SHOPPER_REQUIRED);
  }

  get showPrimaryBillingMethod() {
    const { billingMethodStatus } = this.props.data.me.shop;
    const { showBillingMethodForm } = this.state;

    return !showBillingMethodForm &&
      billingMethodStatus === BillingMethodStatus.COMPLETED &&
      this.primaryBillingCard &&
      this.state.errors.length === 0 &&
      !this.state.isSaving;
  }

  @bind
  async updateMyShopBillingMethod(input: UpdateBillingMethodRequest) {
    this.setState({ isSaving: true, errors: [] });
    try {
      const result = await this.props.updateMyShopBillingMethod({ variables: { input } });
      if (result) {
        this.setState({
          isSaving: false,
          showBillingMethodForm: false,
          billingMethodResult: result.data.updateMyShopBillingMethod,
        }, this.onUpdateBillingMethodComplete);
      }
    } catch (error) {
      this.setState({ isSaving: false, errors: parseGraphqlErrors(error) });
    }
  }

  @bind
  onUpdateBillingMethodComplete() {
    this.handleRedirectShopperRequired();
    this.handleUpdateBillingMethodSuccess();
  }

  @bind
  handleUpdateBillingMethodSuccess() {
    if (this.props.onUpdateBillingMethodSuccess && isBillingMethodResultSuccessful(this.state.billingMethodResult)) {
      this.props.onUpdateBillingMethodSuccess();
    }
  }

  @bind
  openBillingMethodForm() {
    this.setState({ showBillingMethodForm: true });
  }

  @bind
  onThreeDS2Errors(errors: core_apimessages_Error[]) {
    this.setState({
      errors,
      billingMethodResult: {},
      isSaving: false,
    });
  }

  handleRedirectShopperRequired() {
    if (this.props.onRedirectShopperRequired &&
        this.state.billingMethodResult.adyenPaymentResult &&
          this.state.billingMethodResult.adyenPaymentResult.paymentStatus === PaymentStatus.REDIRECT_SHOPPER_REQUIRED) {
      this.props.onRedirectShopperRequired(this.state.billingMethodResult.adyenPaymentResult);
    }
  }

  @bind
  onThreeDS2Result(billingMethodResult: BillingMethodResult) {
    this.setState({ billingMethodResult }, this.handleUpdateBillingMethodSuccess);
  }

  renderThreeDS1Container() {
    const { threeDsOneData } = this.state.billingMethodResult.adyenPaymentResult;

    return (
      <ThreeDS1Container
        redirectUrl={threeDsOneData.redirectUrl}
        paReq={threeDsOneData.paReq}
        md={threeDsOneData.md}
        termUrl={threeDsOneData.termUrl}
      />
    );
  }

  render() {
    if (this.props.data.loading) {
      return (<BillingMethodPlaceholder title={this.props.title} />);
    }

    if (this.props.data.error) {
      return <BillingMethodFormError />;
    }

    if (this.is3ds1Required) {
      return this.renderThreeDS1Container();
    }

    if (this.is3ds2Required) {
      return (
        <ThreeDS2BillingContainer
          title={this.props.title}
          billingMethodResult={this.state.billingMethodResult}
          onBillingMethodResult={this.onThreeDS2Result}
          onError={this.onThreeDS2Errors}
        />
      );
    }

    if (this.showPrimaryBillingMethod) {
      return (
        <PrimaryBillingMethod
          creditCard={this.primaryBillingCard}
          onChangeButtonClick={this.openBillingMethodForm}
        />
      );
    }

    return (
      <BillingMethodForm
        billingMethodStatus={this.props.data.me.shop.billingMethodStatus}
        countries={this.props.data.countries.countries}
        creditCards={this.props.data.me.creditCards}
        defaultOriginCountryCode={this.props.data.me.shop.originCountryCode}
        description={this.props.description}
        isSaving={this.state.isSaving}
        validationErrors={this.state.errors}
        updateMyShopBillingMethod={this.updateMyShopBillingMethod}
        user={this.props.user}
        title={this.props.title}
        showEditURL={this.props.showEditURL}
        returnURL={this.props.returnURL}
      />
    );
  }
}

export default compose(
  withUserContext,
  connectUpdateMyShopBillingMethod,
  connectGetBillingMethod,
)(BillingMethod) as React.ComponentClass<ExternalProps>;



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/billing/billing_method.tsx